import React, { useState, useEffect } from 'react';
import { Container, Card, ListGroup, Alert, Row, Col, Spinner, Button } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import '../App.css';

interface League {
  id: string;
  name: string;
  season: number;
  rosterSize?: number;
  activeRosterSize?: number;
  draftDeadline?: string | null;
  lineupDeadlines?: { [episode: string]: string };
}

interface UserInfo {
  leagueId: string;
}

const RulesPage: React.FC = () => {
  const [leagues, setLeagues] = useState<League[]>([]);
  const [availableSeasons, setAvailableSeasons] = useState<number[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [userInfo, setUserInfo] = useState<UserInfo | null>(null);
  const [selectedSeason, setSelectedSeason] = useState<number>(() => {
    const saved = localStorage.getItem('selectedSeason');
    return saved ? parseInt(saved) : 51;
  });

  const fetchLeaguesAndSeasons = async (season: number) => {
    setIsLoading(true);
    try {
      const [leaguesRes, seasonsRes] = await Promise.all([
        fetch(`${process.env.REACT_APP_API_URL}/api/leagues/all`),
        fetch(`${process.env.REACT_APP_API_URL}/api/seasons`)
      ]);
      if (!leaguesRes.ok) throw new Error('Failed to fetch league deadlines');
      const leaguesData = await leaguesRes.json();
      const filtered = (leaguesData.leagues || []).filter((l: any) => l.season === season);
      setLeagues(filtered);

      if (seasonsRes.ok) {
        const seasonsData = await seasonsRes.json();
        const seasonsList = (seasonsData.seasons || [51, 50, 49]).sort((a: number, b: number) => b - a);
        setAvailableSeasons(seasonsList);
      }
    } catch (err: any) {
      setError(err.message || 'Could not load rules data.');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    const storedUser = localStorage.getItem('fantasyUser');
    if (storedUser) {
      try {
        setUserInfo(JSON.parse(storedUser));
      } catch (e) {
        console.error("Error parsing fantasyUser from localStorage", e);
      }
    }

    fetchLeaguesAndSeasons(selectedSeason);

    const handleSeasonChange = () => {
      const saved = localStorage.getItem('selectedSeason');
      const newSeason = saved ? parseInt(saved) : 51;
      setSelectedSeason(newSeason);
      fetchLeaguesAndSeasons(newSeason);
    };
    window.addEventListener('seasonChanged', handleSeasonChange);
    return () => window.removeEventListener('seasonChanged', handleSeasonChange);
  }, []);

  const handleSelectSeason = (season: number) => {
    setSelectedSeason(season);
    localStorage.setItem('selectedSeason', season.toString());
    window.dispatchEvent(new Event('seasonChanged'));
    fetchLeaguesAndSeasons(season);
  };

  const formatDate = (dateString: string | null | undefined) => {
    if (!dateString) return 'Not Set';
    return new Date(dateString).toLocaleString();
  };

  const isNewFormat = selectedSeason >= 51;

  return (
    <Container className="mt-5 mb-5">
      <Card className="shadow-sm home-card">
        <Card.Header as="h4" className="card-title-custom text-center">
          Fantasy Survivor Rules (Season {selectedSeason})
        </Card.Header>
        <Card.Body>
          {/* Season Switcher Pills */}
          {availableSeasons.length > 1 && (
            <div className="d-flex justify-content-center align-items-center gap-2 mb-4 flex-wrap">
              <span className="text-muted fw-bold me-1">View Rules for:</span>
              {availableSeasons.map(s => (
                <Button
                  key={s}
                  size="sm"
                  variant={selectedSeason === s ? 'primary' : 'outline-secondary'}
                  onClick={() => handleSelectSeason(s)}
                >
                  Season {s} {s >= 51 && '(New Format)'}
                </Button>
              ))}
            </div>
          )}

          {error && <Alert variant="danger">{error}</Alert>}
          {isLoading ? (
            <div className="text-center p-4"><Spinner animation="border" /></div>
          ) : (
            leagues.length > 0 ? (
              leagues.map(league => (
                <Alert key={league.id} variant="warning" className="text-center">
                  <h5><i className="bi bi-exclamation-triangle-fill"></i> {league.name} Deadline</h5>
                  Draft Selections Due By: <strong>{formatDate(league.draftDeadline)}</strong>
                </Alert>
              ))
            ) : (
              <Alert variant="info" className="text-center">No active leagues found for Season {selectedSeason}.</Alert>
            )
          )}

          {/* SEASON 51+ (NEW FORMAT) */}
          {isNewFormat ? (
            <>
              <section className="mb-4">
                <h5 className="text-primary">Overview (Season {selectedSeason})</h5>
                <p>
                  Each participant drafts a full tribe of <strong>5 castaways</strong> from Survivor Season {selectedSeason}. 
                  Each week, you designate <strong>3 Active players</strong> to earn points for that episode while your other 
                  <strong> 2 players sit on your Bench</strong>. Points are awarded based on your Active players' in-game 
                  performance throughout the season. The highest point total at the end wins!
                </p>
              </section>

              <section className="mb-4">
                <h5 className="text-primary">5-Player Roster & Weekly Active Lineups</h5>
                <p>Season {selectedSeason} replaces the old static draft and mid-season waiver swaps with a dynamic active/bench lineup system:</p>
                <ul>
                  <li>
                    <strong>Initial Draft (5 Players):</strong> Before the draft deadline, select 5 castaways to form your team roster.
                  </li>
                  <li>
                    <strong>3 Active / 2 Bench:</strong> Each week, select 3 active players to represent your scoring team for that episode. The remaining 2 players remain on your bench as reserves and do not accumulate points that week.
                  </li>
                  <li>
                    <strong>Weekly Lineup Deadline (7:00 PM ET):</strong> Lineup changes between your Bench and Active slots are unlimited each week, but must be locked in before <strong>7:00 PM ET on the day of the episode</strong> (beginning with Episode 2).
                  </li>
                  <li>
                    <strong>Only Active Players Score:</strong> You only earn points for the castaways you have designated as Active during that episode.
                  </li>
                  <li>
                    <strong>Voted Out / Elimination:</strong>
                    <ul>
                      <li>If an active player is voted out or medically evacuated, they remain in your lineup for that episode and score any points earned up to elimination. For subsequent episodes, they are eliminated and can no longer be activated. You must promote an eligible player from your bench to fill the active slot.</li>
                      <li>If a benched player is voted out, they are marked as eliminated on your bench and can no longer be activated.</li>
                    </ul>
                  </li>
                  <li>
                    <strong>No Outside Player Swaps:</strong> Because your roster has 5 players with weekly active/bench flexibility, mid-season player swaps and medical waiver replacements are no longer needed.
                  </li>
                  <li>
                    <strong>Final 5 Global Lock:</strong> Once only 5 non-eliminated castaways remain globally in the Survivor competition, all lineup changes lock permanently for the rest of the season.
                  </li>
                </ul>

                <h6 className="fw-bold text-dark mt-3">🪙 Coin Flip Twist (Season 51+)</h6>
                <p>
                  A one-time coin flip will be offered to one Survivor. The high-risk, high-reward twist is scored as follows:
                </p>
                <ul>
                  <li>
                    <strong>Correct Flip (Win):</strong> The Survivor earns <strong>+4 points</strong> for the Coin Flip Win and receives a Hidden Immunity Idol (<strong>+4 points</strong> for Idol Obtained), resulting in <strong>+8 total points</strong>.
                  </li>
                  <li>
                    <strong>Incorrect Flip (Loss):</strong> The Survivor is <strong>immediately voted out</strong> of the competition. If this player was chosen for your weekly Elimination Pick, you will receive the <strong>+3 bonus points</strong>.
                  </li>
                </ul>

                <div className="d-flex gap-2 flex-wrap mt-3">
                  <Link to={`/draft?season=${selectedSeason}`} className="btn btn-outline-primary">Manage Your Draft & Lineup</Link>
                  <Link to={`/mock-draft?season=${selectedSeason}`} className="btn btn-outline-success">Practice with Mock Draft</Link>
                </div>
              </section>
            </>
          ) : (
            /* SEASONS <= 50 (LEGACY FORMAT) */
            <>
              <section className="mb-4">
                <h5 className="text-primary">Overview (Season {selectedSeason} Legacy Format)</h5>
                <p>Each participant selects <strong>3 castaways</strong> from Survivor Season {selectedSeason}. Points are awarded based on their performance throughout the season. The highest point total at the end wins!</p>
              </section>

              <section className="mb-4">
                <h5 className="text-primary">Player Swaps</h5>
                <p>A single (1) player swap is allowed per entry. This swap can be made anytime before <strong>7pm ET on the day of Episode 10</strong>.</p>
                <ul>
                  <li>Point accumulation for the new player begins from the start of Episode 10.</li>
                  <li>Points are NOT retroactive.</li>
                  <li>You will retain the points earned by your original castaway up to the swap.</li>
                  <li>The swapped-out player will no longer contribute to your score.</li>
                </ul>
                <Link to="/swaps" className="btn btn-outline-warning">Go to Player Swap Page</Link>
              </section>

              <section className="mb-4">
                <h5 className="text-primary">Medical Evacuation</h5>
                <p>If a castaway is medically evacuated from the game, you are entitled to a special player swap. This does not count against your single regular season swap.</p>
                <ul>
                  <li>The swap must be made before <strong>7pm ET on the day of the following episode</strong>.</li>
                  <li>Your evacuated player will retain all points earned up to their departure.</li>
                  <li>The new player you select will begin accumulating points from the episode following the swap. Points are not retroactive.</li>
                </ul>
                <Link to="/swaps" className="btn btn-outline-warning">Go to Player Swap Page</Link>
              </section>
            </>
          )}

          {/* COMMON SECTIONS */}
          <section className="mb-4">
            <h5 className="text-primary">Weekly Elimination Pick</h5>
            <p>Each week, you must pick which castaway you believe will be voted out. A correct pick will earn you <strong>3 bonus points</strong>.</p>
            <ul>
              <li>Picks must be submitted before <strong>7:00 PM ET on the day of the episode</strong>.</li>
              <li>Failure to submit a pick will result in zero points for that week's elimination.</li>
              <li>This contest is open until Episode 10.</li>
            </ul>
            {userInfo && userInfo.leagueId && (
              <Link to={`/leagues/${userInfo.leagueId}/elimination-pick`} className="btn btn-outline-info">Go to Weekly Elimination Pick Page</Link>
            )}
          </section>

          <section className="mb-4">
            <h5 className="text-primary">Entry Fee & Payouts</h5>
            <Row>
              <Col md={6} className="mb-3">
                <Card bg="light">
                  <Card.Body>
                    <Card.Title as="h6">Entry Fee: $10</Card.Title>
                    <ListGroup variant="flush">
                      <ListGroup.Item><strong>Venmo:</strong> @Goldberg-Jonathan (Preferred)</ListGroup.Item>
                      <ListGroup.Item><strong>PayPal:</strong> @jonathangoldberg1</ListGroup.Item>
                      <ListGroup.Item><strong>CashApp:</strong> $goldbergjontg</ListGroup.Item>
                    </ListGroup>
                  </Card.Body>
                </Card>
              </Col>
              <Col md={6} className="mb-3">
                <Card bg="light">
                  <Card.Body>
                    <Card.Title as="h6">Payout Structure</Card.Title>
                    <ListGroup variant="flush">
                      <ListGroup.Item>1st Place: 75% of the pot</ListGroup.Item>
                      <ListGroup.Item>2nd Place: 20% of the pot</ListGroup.Item>
                      <ListGroup.Item>3rd Place: 5% of the pot</ListGroup.Item>
                    </ListGroup>
                  </Card.Body>
                </Card>
              </Col>
            </Row>
          </section>

          <section className="mb-4">
            <h5 className="text-primary">Scoring Details</h5>
            <p>Specific point values for various in-game actions are detailed on the Scoring page.</p>
            <div className="text-center">
              <Link to="/scoring" className="btn btn-info custom-btn">View Full Scoring Rubric</Link>
            </div>
          </section>
          
          <div className="text-center mt-4">
            <Link to="/" className="btn btn-secondary">Back to Home</Link>
          </div>
        </Card.Body>
      </Card>
    </Container>
  );
};

export default RulesPage;